import { AlertTriangle, PackagePlus } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/constants/routes";
import { MyListingCard } from "@/features/listings/components/MyListingCard";
import { useMyListings } from "@/features/listings/hooks/useMyListings";
import { useDeleteListing, useUpdateListing } from "@/features/listings/hooks/useUpdateListing";
import type { ProductStatus } from "@/features/marketplace/types/product";
import { ApiError } from "@/types/api";
import { cn } from "@/utils/cn";

const TABS: Array<{ value: ProductStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "sold", label: "Sold" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

/**
 * docs/architecture/claude/ui-architecture.md's "My Listings Dashboard
 * (seller)" — `?tab=` is a query param, not separate routes
 * (`docs/ui/routes.md`'s own decision), so a filtered view stays
 * shareable/bookmarkable without multiplying route entries.
 */
export function MyListingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as ProductStatus | "all" | null) ?? "all";
  const listingsQuery = useMyListings(tab === "all" ? {} : { status: tab });
  const updateListingMutation = useUpdateListing();
  const deleteListingMutation = useDeleteListing();

  function setTab(value: ProductStatus | "all") {
    if (value === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  }

  function handleMarkSold(id: string) {
    updateListingMutation.mutate(
      { id, input: { status: "sold" } },
      {
        onSuccess: () => showToast.success("Marked as sold."),
        onError: (error) =>
          showToast.error(error instanceof ApiError ? error.message : "Couldn't update listing."),
      },
    );
  }

  function handleDelete(id: string) {
    deleteListingMutation.mutate(id, {
      onSuccess: () => showToast.success("Listing deleted."),
      onError: (error) =>
        showToast.error(error instanceof ApiError ? error.message : "Couldn't delete listing."),
    });
  }

  return (
    <div className="mx-auto max-w-[900px] px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-h1 text-foreground">My Listings</h1>
        <Button type="button" asChild>
          <Link to={PROTECTED_ROUTES.sellMedia}>
            <PackagePlus className="h-4 w-4" aria-hidden="true" />
            New listing
          </Link>
        </Button>
      </div>

      <div className="mb-6 flex gap-1 border-b border-border">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={cn(
              "border-b-2 px-3 py-2 text-body-sm",
              tab === item.value
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {listingsQuery.isPending && (
        <p className="text-body text-muted-foreground">Loading your listings…</p>
      )}

      {listingsQuery.isError && (
        <EmptyState
          icon={AlertTriangle}
          heading="Couldn't load your listings"
          description={
            listingsQuery.error instanceof ApiError
              ? listingsQuery.error.message
              : "Something went wrong. Please try again."
          }
          action={{ label: "Retry", onClick: () => void listingsQuery.refetch() }}
        />
      )}

      {listingsQuery.data && listingsQuery.data.length === 0 && (
        <EmptyState
          icon={PackagePlus}
          heading="No listings yet"
          description="Create your first listing to start selling on BazaarX."
          action={{
            label: "Create a listing",
            onClick: () => navigate(PROTECTED_ROUTES.sellMedia),
          }}
        />
      )}

      {listingsQuery.data && listingsQuery.data.length > 0 && (
        <div className="space-y-3">
          {listingsQuery.data.map((product) => (
            <MyListingCard
              key={product.id}
              product={product}
              onMarkSold={handleMarkSold}
              isMarkingSold={
                updateListingMutation.isPending && updateListingMutation.variables?.id === product.id
              }
              onDelete={handleDelete}
              isDeleting={
                deleteListingMutation.isPending && deleteListingMutation.variables === product.id
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
